import React, { useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Typography, Button } from '@material-ui/core';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationSelectorSystemClassOnlyPattern,
    selectApplicationSelectorSystemClassOnlyReplacer,
    selectApplicationSelectorSystemIdBlacklist,
    selectApplicationSelectorSystemClassNameBlacklist,
    selectApplicationSelectorSystemCustomWhitelist,
    selectApplicationSelectorSystemExcludedLocations,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormInput from 'Console/components/common/form/FormInput';

const styles = (theme) => ({
    root: {
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
    },
    title: {
        display: 'inline-block',
        'font-weight': 'bold',
        'padding-bottom': theme.typography.pxToRem(20),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        paddingBottom: theme.typography.pxToRem(34),
    },
    inputItems: {
        width: '50%',
        [theme.breakpoints.between('xs', 'lg')]: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    btn: {
        marginBottom: theme.typography.pxToRem(65),
        marginTop: theme.typography.pxToRem(20),
    },
    formInput: {
        width: '33%',
        marginRight: theme.typography.pxToRem(35),
        marginBottom: '3%',
        [theme.breakpoints.between('md', 'lg')]: {
            marginRight: 0,
        },
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: '5%',
        },
    },
});

const SelectorSystem = ({ classes, intl, form }) => {
    const dispatch = useDispatch();

    const handleSaveSettings = useCallback(() => {
        const { pattern, replacer, attributes, excludedLocations } = form.getFieldValue('selector');
        dispatch(
            updateDetailSettings(
                {
                    'selectorSystem.classOnly.pattern': pattern || '',
                    'selectorSystem.classOnly.replacer': replacer || '',
                    'selectorSystem.attributes.id.blacklist': attributes.id.blacklist || '',
                    'selectorSystem.attributes.className.blacklist':
                        attributes.className.blacklist || '',
                    'selectorSystem.attributes.custom.whitelist': attributes.custom.whitelist || '',
                    'selectorSystem.excludedLocations': excludedLocations ? [excludedLocations] : [],
                },
                {
                    success: `${intl.formatMessage({ id: 'Notification.save' })}`,
                    fail: `${intl.formatMessage({ id: 'Notification.fail' })}`,
                }
            )
        );
    }, [dispatch, form, intl]);

    const pattern = useSelector(selectApplicationSelectorSystemClassOnlyPattern);
    const replacer = useSelector(selectApplicationSelectorSystemClassOnlyReplacer);
    const idBlacklist = useSelector(selectApplicationSelectorSystemIdBlacklist);
    const classNameBlacklist = useSelector(selectApplicationSelectorSystemClassNameBlacklist);
    const customWhitelist = useSelector(selectApplicationSelectorSystemCustomWhitelist);
    const excludedLocations = useSelector(selectApplicationSelectorSystemExcludedLocations);

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.selectorSystem.title" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.message" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.message.1" />
                        <br />
                        <br />
                        <FormattedMessage id="Settings.selectorSystem.message.2" />
                        <br />
                        <br />
                        <FormattedMessage id="Settings.selectorSystem.message.3" />
                        <br />
                        <FormattedMessage id="Settings.selectorSystem.message.4" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.selectorSystem.classOnly.title" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.classOnly.message" />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="flex-end" className={classes.inputItems}>
                    <Grid className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            form={form}
                            type="text"
                            initialValue={pattern}
                            formItemKey="selector.pattern"
                            label={<FormattedMessage id="Settings.selectorSystem.label" />}
                        />
                    </Grid>

                    <Grid className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            form={form}
                            type="text"
                            initialValue={replacer}
                            formItemKey="selector.replacer"
                            label={<FormattedMessage id="Settings.selectorSystem.label2" />}
                        />
                    </Grid>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.selectorSystem.idBlacklist.title" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.idBlacklist.message" />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="flex-end" className={classes.inputItems}>
                    <Grid item xs={12} className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            multiline
                            rows={2}
                            form={form}
                            type="text"
                            initialValue={idBlacklist}
                            formItemKey="selector.attributes.id.blacklist"
                            placeholder={intl.formatMessage({
                                id: 'Settings.selectorSystem.idBlacklist.placeholder',
                            })}
                        />
                    </Grid>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.selectorSystem.classNameBlacklist.title" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.classNameBlacklist.message" />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="flex-end" className={classes.inputItems}>
                    <Grid item xs={12} className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            multiline
                            rows={2}
                            form={form}
                            type="text"
                            initialValue={classNameBlacklist}
                            formItemKey="selector.attributes.className.blacklist"
                            placeholder={intl.formatMessage({
                                id: 'Settings.selectorSystem.classNameBlacklist.placeholder',
                            })}
                        />
                    </Grid>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.selectorSystem.customWhitelist.title" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.customWhitelist.message" />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="flex-end" className={classes.inputItems}>
                    <Grid item xs={12} className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            multiline
                            rows={2}
                            form={form}
                            type="text"
                            initialValue={customWhitelist}
                            formItemKey="selector.attributes.custom.whitelist"
                            placeholder={intl.formatMessage({
                                id: 'Settings.selectorSystem.customWhitelist.placeholder',
                            })}
                        />
                    </Grid>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.selectorSystem.excludedLocations.title" />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="flex-end" className={classes.inputItems}>
                    <Grid item xs={12} className={classes.formInput}>
                        <FormInput
                            fullWidth={false}
                            multiline
                            rows={2}
                            form={form}
                            type="text"
                            initialValue={excludedLocations.length && excludedLocations[0]}
                            formItemKey="selector.excludedLocations"
                            placeholder={intl.formatMessage({
                                id: 'Settings.selectorSystem.excludedLocations.placeholder',
                            })}
                        />
                    </Grid>
                </Grid>

                <Grid container alignItems="center">
                    <Typography variant="button">
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            onClick={handleSaveSettings}
                            className={classes.btn}
                        >
                            <FormattedMessage id="Buttons.save" />
                        </Button>
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(SelectorSystem));
